import React, { useEffect, useState } from "react";
import "./OrganizationalAdaptationsSection.scss";
import ContentSection from "../components/ContentSection";
import SectionHeader from "../components/SectionHeader";
import TwoColumnLayout from "../components/TwoColumnLayout";
import BarChart from "../components/BarChart";

const ORGANIZATIONAL_RESPONSE_DATA = [
  { type: "Increased fundraising efforts", avg: 43 },
  { type: "Expanded partnerships", avg: 36 },
  { type: "Streamlined services to reduce costs", avg: 29 },
];

const ORGANIZATIONAL_RESPONSE_COLORS = [
  "#24B09A", // teal
  "#84c141", // medium green
  "#ff8c42", // orange
];

const OrganizationalAdaptationsSection: React.FC = () => {
  const [chartWidth, setChartWidth] = useState(600);
  const [chartLeftMargin, setChartLeftMargin] = useState(200);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 480) {
        setChartWidth(300);
        setChartLeftMargin(140);
      } else if (width <= 768) {
        setChartWidth(500);
        setChartLeftMargin(160);
      } else {
        setChartWidth(600);
        setChartLeftMargin(200);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const leftContent = (
    <div className="organizational-adaptations-section-content">
      <p>
        This ability to adapt isn’t new. Research shows that in the face of
        unstable or shrinking federal funding, nonprofits frequently rely on
        community collaboration, diversified fundraising, and flexible service
        models to stay afloat.
      </p>
      <p>
        Among survey respondents, 43% said they increased fundraising efforts,
        36% expanded partnerships, and 29% streamlined services to reduce costs.
        Others have restructured staffing models to align with reduced budgets.
        This applies to organizations that have directly experienced funding
        cuts as well as those that have not. These moves illustrate how
        providers are continually pivoting and innovating.
      </p>
      <p>
        However, experts caution that constant adaptation can come at a cost:
        when organizations are perpetually transitioning, they often operate in
        crisis mode, prioritizing short-term survival over building long-term
        capacity, which can also lead to mission drift.
      </p>
    </div>
  );

  const rightContent = (
    <div className="organizational-adaptations-section-right-content">
      <div className="chart-header">
        <h4 className="chart-title">Organizational response strategies</h4>
        <p className="chart-subtitle">
          How organizations adapt to funding disruptions and policy changes.
        </p>
      </div>
      <BarChart
        data={ORGANIZATIONAL_RESPONSE_DATA}
        maxAvg={50}
        width={chartWidth}
        height={280}
        leftMargin={chartLeftMargin}
        colors={ORGANIZATIONAL_RESPONSE_COLORS}
        barLabel={(d) => `${d.avg}%`}
        className="responsive-chart"
      />
    </div>
  );

  return (
    <ContentSection className="organizational-adaptations-section">
      <SectionHeader
        title="Federal Funding Disruptions: Organizational Adaptations"
        description="Organizations are responding to funding disruptions and policy changes with a range of adaptations."
      />
      <TwoColumnLayout leftContent={leftContent} rightContent={rightContent} />

      <h3 className="takeaways-heading">TAKEAWAYS</h3>
      <ol className="takeaways-grid">
        <li className="takeaway-item">
          <strong>
            Diversification in revenue streams in more important than ever
          </strong>
          <p>
            Uncertainty is not new to reentry workforce programs. Since the
            early 2000’s, federal support for reentry workforce initiatives has
            been consistent and growing. In 2025, it can no longer be assumed to
            be either. Expanding funding beyond federal contracts by
            strengthening individual donor bases, cultivating corporate
            partnerships, and exploring earned income strategies, is more
            important now than ever.
          </p>
        </li>
        <li className="takeaway-item">
          <strong>
            Take a greater look at cultivating financial resilience
          </strong>
          <p>
            Through this period of volatility and uncertainty, we are seeing the
            reentry workforce field controlling non-essential costs, building
            operational reserves, and identifying core services to ensure
            mission- critical services.
          </p>
        </li>
        <li className="takeaway-item">
          <strong>Invest in building an ecosystem</strong>
          <p>
            When resources are scarce and there is more competition for those
            that remain, there can be tendencies to revert to silos. However,
            this is a time it is critical to share best practices, coordinate
            with other providers to best utilize resources, and utilize systems
            thinking to ensure opportunities for justice-involved job seekers.
          </p>
        </li>
        <li className="takeaway-item">
          <strong>Discipline is needed to guard against mission drift</strong>
          <p>
            While adaptability is essential, constant pivots in response to
            funding disruptions and policy shifts can cause organizations to
            drift from their core missions. Regularly revisiting organizational
            values and aligning new opportunities with core goals helps ensure
            survival strategies strengthen the mission, rather than dilute it.
          </p>
        </li>
      </ol>
    </ContentSection>
  );
};

export default OrganizationalAdaptationsSection;
